/**
 * OWL2 Entity Types
 *
 * Defines the six OWL2 entity types (Class, Datatype, ObjectProperty,
 * DataProperty, AnnotationProperty, NamedIndividual) and Individual types.
 */

import type { IRI } from './IRI.ts';

// =============================================================================
// Entity Type Definitions
// =============================================================================

/**
 * All possible entity types in OWL2.
 */
export type EntityType =
    | 'Class'
    | 'Datatype'
    | 'ObjectProperty'
    | 'DataProperty'
    | 'AnnotationProperty'
    | 'NamedIndividual';

/**
 * Base interface for all entities.
 */
interface EntityBase {
    readonly entityType: EntityType;
    readonly iri: IRI;
}

/**
 * OWL2 Class entity.
 * Represents a class (concept) in an ontology.
 */
export interface ClassEntity extends EntityBase {
    readonly entityType: 'Class';
}

/**
 * OWL2 Datatype entity.
 * Represents a datatype (value type) in an ontology.
 */
export interface DatatypeEntity extends EntityBase {
    readonly entityType: 'Datatype';
}

/**
 * OWL2 ObjectProperty entity.
 * Represents a property that relates individuals to individuals.
 */
export interface ObjectPropertyEntity extends EntityBase {
    readonly entityType: 'ObjectProperty';
}

/**
 * OWL2 DataProperty entity.
 * Represents a property that relates individuals to literal values.
 */
export interface DataPropertyEntity extends EntityBase {
    readonly entityType: 'DataProperty';
}

/**
 * OWL2 AnnotationProperty entity.
 * Represents a property used for annotations (metadata).
 */
export interface AnnotationPropertyEntity extends EntityBase {
    readonly entityType: 'AnnotationProperty';
}

/**
 * OWL2 NamedIndividual entity.
 * Represents a named individual (instance) in an ontology.
 */
export interface NamedIndividualEntity extends EntityBase {
    readonly entityType: 'NamedIndividual';
}

/**
 * Discriminated union of all OWL2 entity types.
 * The 'entityType' field distinguishes between entity types.
 */
export type Entity =
    | ClassEntity
    | DatatypeEntity
    | ObjectPropertyEntity
    | DataPropertyEntity
    | AnnotationPropertyEntity
    | NamedIndividualEntity;

// =============================================================================
// Individual Type Definitions
// =============================================================================

/**
 * Named individual - has an IRI identifier.
 */
export interface NamedIndividual {
    readonly type: 'NamedIndividual';
    readonly iri: IRI;
}

/**
 * Anonymous individual - identified by a node ID (blank node).
 */
export interface AnonymousIndividual {
    readonly type: 'AnonymousIndividual';
    readonly nodeId: string;
}

/**
 * Discriminated union of individual types.
 * An individual can be either named (with IRI) or anonymous (with nodeId).
 */
export type Individual = NamedIndividual | AnonymousIndividual;

// =============================================================================
// Entity Factory Functions
// =============================================================================

/**
 * Create a Class entity.
 */
export function classEntity(iri: IRI): ClassEntity {
    return { entityType: 'Class', iri };
}

/**
 * Create a Datatype entity.
 */
export function datatypeEntity(iri: IRI): DatatypeEntity {
    return { entityType: 'Datatype', iri };
}

/**
 * Create an ObjectProperty entity.
 */
export function objectPropertyEntity(iri: IRI): ObjectPropertyEntity {
    return { entityType: 'ObjectProperty', iri };
}

/**
 * Create a DataProperty entity.
 */
export function dataPropertyEntity(iri: IRI): DataPropertyEntity {
    return { entityType: 'DataProperty', iri };
}

/**
 * Create an AnnotationProperty entity.
 */
export function annotationPropertyEntity(iri: IRI): AnnotationPropertyEntity {
    return { entityType: 'AnnotationProperty', iri };
}

/**
 * Create a NamedIndividual entity.
 */
export function namedIndividualEntity(iri: IRI): NamedIndividualEntity {
    return { entityType: 'NamedIndividual', iri };
}

// =============================================================================
// Individual Factory Functions
// =============================================================================

/**
 * Create a named individual.
 */
export function namedIndividual(iri: IRI): NamedIndividual {
    return { type: 'NamedIndividual', iri };
}

/**
 * Create an anonymous individual.
 */
export function anonymousIndividual(nodeId: string): AnonymousIndividual {
    return { type: 'AnonymousIndividual', nodeId };
}

// =============================================================================
// Entity Type Guards
// =============================================================================

/**
 * Check if an entity is a Class.
 */
export function isClassEntity(entity: Entity): entity is ClassEntity {
    return entity.entityType === 'Class';
}

/**
 * Check if an entity is a Datatype.
 */
export function isDatatypeEntity(entity: Entity): entity is DatatypeEntity {
    return entity.entityType === 'Datatype';
}

/**
 * Check if an entity is an ObjectProperty.
 */
export function isObjectPropertyEntity(entity: Entity): entity is ObjectPropertyEntity {
    return entity.entityType === 'ObjectProperty';
}

/**
 * Check if an entity is a DataProperty.
 */
export function isDataPropertyEntity(entity: Entity): entity is DataPropertyEntity {
    return entity.entityType === 'DataProperty';
}

/**
 * Check if an entity is an AnnotationProperty.
 */
export function isAnnotationPropertyEntity(entity: Entity): entity is AnnotationPropertyEntity {
    return entity.entityType === 'AnnotationProperty';
}

/**
 * Check if an entity is a NamedIndividual.
 */
export function isNamedIndividualEntity(entity: Entity): entity is NamedIndividualEntity {
    return entity.entityType === 'NamedIndividual';
}

// =============================================================================
// Individual Type Guards
// =============================================================================

/**
 * Check if an individual is a named individual.
 */
export function isNamedIndividual(individual: Individual): individual is NamedIndividual {
    return individual.type === 'NamedIndividual';
}

/**
 * Check if an individual is an anonymous individual.
 */
export function isAnonymousIndividual(individual: Individual): individual is AnonymousIndividual {
    return individual.type === 'AnonymousIndividual';
}
